<template>
  <v-navigation-drawer
    :value="value"
    app
    width="272"
    class="sidebar"
    @input="$emit('input', $event)"
  >
    <!-- one scrolling column: on a short screen the help box is reached by scrolling, on a tall one it sits at the bottom -->
    <div class="sidebar__column">
      <router-link to="/" class="sidebar__brand" aria-label="Suport — pagina principală">
        <span class="sidebar__crest">
          <img :src="crest" alt="" />
        </span>
        <span class="sidebar__wordmark">
          <span class="sidebar__name">Suport</span>
          <span class="sidebar__org">USARB, Direcția TI</span>
        </span>
      </router-link>

      <nav class="sidebar__nav" aria-label="Navigare principală">
        <router-link
          v-for="link in filteredLinks"
          :key="link.id"
          :to="link.url"
          :exact="!!link.exact"
          class="sidebar__link"
          active-class="is-active"
          exact-active-class="is-active"
        >
          <v-icon class="sidebar__icon">{{ link.icon }}</v-icon>
          <span class="sidebar__label">{{ link.label }}</span>
          <span
            v-if="link.badge === 'approvals' && approvalCount"
            class="sidebar__badge"
            :aria-label="approvalCount + ' de aprobat'"
          >{{ approvalCount }}</span>
        </router-link>
      </nav>

      <div class="sidebar__help">
        <div class="sidebar__help-title">Asistență DTI</div>
        <div>Blocul 3, of. 369</div>
        <a href="tel:+37323152388">tel. 52-388</a>
        <a href="mailto:operator.dti@usarb.md">operator.dti@usarb.md</a>
        <a href="/ghid/Suport-Ghid-complet.pdf" target="_blank" rel="noopener" class="sidebar__guide">
          <v-icon small>mdi-book-open-variant</v-icon>Ghidul utilizatorului (PDF)
        </a>
        <a href="/ghid/Suport-Ghid-complet-RU.pdf" target="_blank" rel="noopener" class="sidebar__guide">
          <v-icon small>mdi-book-open-variant</v-icon>Руководство (PDF, RU)
        </a>
        <a v-if="showInstall" href="#" role="button" class="sidebar__guide" @click.prevent="install">
          <v-icon small>mdi-download</v-icon>Instalează aplicația
        </a>
        <v-dialog v-model="iosHint" max-width="360">
          <v-card class="install-hint">
            <v-card-title class="install-hint__title">
              <v-icon left color="#2d8659">mdi-cellphone-arrow-down</v-icon>Instalează aplicația
            </v-card-title>
            <v-card-text class="install-hint__text">
              Apăsați Partajare <v-icon small color="#2d8659">mdi-export-variant</v-icon>, apoi «Adaugă pe ecranul principal».
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text color="#2d8659" @click="iosHint = false">Am înțeles</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import linksData from "./links.json";
import { pwa, isIos, promptInstall } from "@/helper/pwa";

export default {
  name: "SideBar",
  props: {
    value: { type: Boolean, default: null },
  },
  data() {
    return {
      links: linksData,
      crest: require("@/assets/pictures/LogoUsarb.png"),
      iosHint: false,
    };
  },
  methods: {
    install() {
      if (pwa.canPrompt) promptInstall();
      else this.iosHint = true;
    },
  },
  computed: {
    // only when installable: Chrome/Edge offered it (beforeinstallprompt), or iOS (manual hint)
    showInstall() {
      return !pwa.installed && (pwa.canPrompt || isIos);
    },
    userRole() {
      return this.$store.getters.getUser?.role;
    },
    approvalCount() {
      return this.$store.getters.approvalCount;
    },
    filteredLinks() {
      return this.links.filter((link) => !link.meta?.roles || link.meta.roles.includes(this.userRole));
    },
  },
};
</script>

<style scoped>
.sidebar.v-navigation-drawer {
  background: var(--pine-950) !important;
  border: none;
  /* Vuetify sets 100vh, which on phones is taller than the visible screen */
  height: 100% !important;
}
@supports (height: 100dvh) {
  .sidebar.v-navigation-drawer {
    height: 100dvh !important;
  }
}
.sidebar__column {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}
.sidebar ::v-deep .v-navigation-drawer__border {
  display: none;
}
.sidebar__brand {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 22px 20px 20px;
  text-decoration: none;
}
.sidebar__crest {
  flex: none;
  display: grid;
  place-items: center;
  width: 58px;
  height: 46px;
  border-radius: 12px;
  background: #f4faf6;
}
.sidebar__crest img {
  width: 48px;
  height: auto;
}
.sidebar__wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1.1;
}
.sidebar__name {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 1.7rem;
  letter-spacing: -0.03em;
  color: #fff;
}
.sidebar__org {
  margin-top: 4px;
  font-size: 0.86rem;
  color: rgba(195, 233, 211, 0.75);
}
.sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px 14px;
}
.sidebar__link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  min-height: 50px;
  padding: 0 14px;
  border-radius: 12px;
  color: rgba(255, 255, 255, 0.74) !important;
  text-decoration: none;
  font-size: 1.08rem;
  font-weight: 600;
  transition: background-color 0.15s, color 0.15s;
}
.sidebar__link:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #fff !important;
}
.sidebar__link.is-active {
  background: rgba(127, 209, 163, 0.14);
  color: #fff !important;
}
.sidebar__link.is-active::before {
  content: "";
  position: absolute;
  left: -14px;
  top: 10px;
  bottom: 10px;
  width: 4px;
  border-radius: 0 4px 4px 0;
  background: var(--leaf);
}
.sidebar__icon.v-icon {
  color: inherit;
  font-size: 24px;
}
.sidebar__link.is-active .sidebar__icon.v-icon {
  color: var(--leaf);
}
.sidebar__label {
  flex: 1;
}
.sidebar__badge {
  min-width: 28px;
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--leaf);
  color: var(--pine-900);
  font-size: 0.85rem;
  font-weight: 800;
  text-align: center;
}
.sidebar__help {
  margin: auto 14px calc(18px + env(safe-area-inset-bottom, 0px));
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.5;
  display: flex;
  flex-direction: column;
}
.sidebar__help-title {
  color: #fff;
  font-weight: 700;
  margin-bottom: 2px;
}
.sidebar__help a {
  color: var(--leaf-200) !important;
  text-decoration: none;
}
.sidebar__help a:hover {
  text-decoration: underline;
}
.sidebar__guide {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-weight: 700;
}
.sidebar__guide .v-icon {
  color: inherit !important;
}
html.density-compact .sidebar__brand {
  padding: 14px 18px 12px;
}
html.density-compact .sidebar__nav {
  gap: 2px;
  padding: 6px 12px;
}
html.density-compact .sidebar__link {
  min-height: 40px;
  font-size: 1rem;
}
html.density-compact .sidebar__help {
  padding: 10px 14px;
  line-height: 1.4;
}
.install-hint.v-card {
  border-radius: 12px;
}
.install-hint__title {
  font-size: 1.1rem;
  font-weight: 700;
}
.install-hint__text {
  font-size: 1rem;
  line-height: 1.6;
  color: var(--ink) !important;
}
</style>
